import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { foodRecipesApi, foodsApi } from '../services/api';
import {DietNutritionSummary} from './DietNutritionSummary';

const mealOrder = ['Colazione', 'Spuntino mattutino', 'Spuntino', 'Pranzo', 'Spuntino pomeridiano', 'Merenda', 'Cena', 'Dopo cena', 'Spuntino serale', 'Altro'];
const roles = ['', 'Bevanda', 'Fonte proteica', 'Fonte glucidica', 'Verdura', 'Frutta', 'Grassi', 'Piatto completo', 'Integrazione'];
const units = ['', 'g', 'ml', 'porzione', 'cucchiaio', 'cucchiaino', 'tazza', 'bicchiere', 'pezzo', 'fetta', 'vasetto', 'mestolo', 'piatto', 'q.b.'];
let alternativeClientSequence = 0;
const nextAlternativeClientId = () => `diet-alternative-${++alternativeClientSequence}`;

function Icon({ name }) {
  const paths = {
    plus: <><path d="M12 5v14M5 12h14"/></>,
    search: <><circle cx="11" cy="11" r="6.5"/><path d="m16 16 4 4"/></>,
    trash: <><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5"/></>,
    spark: <><path d="m12 3 1.2 4.1L17 9l-3.8 1.9L12 15l-1.2-4.1L7 9l3.8-1.9L12 3Z"/><path d="m18 15 .7 2.3L21 18l-2.3.7L18 21l-.7-2.3L15 18l2.3-.7L18 15Z"/></>,
    check: <path d="m5 12 4 4L19 6"/>,
    barcode: <><path d="M4 5v14M7 5v14M11 5v14M14 5v14M16.5 5v14M20 5v14"/></>
  };
  return <svg className="diet-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{paths[name]}</svg>;
}

function normalizeRow(row, fallbackDay = 1) {
  const alternativeOptions = Array.isArray(row.alternativeOptions) && row.alternativeOptions.length
    ? row.alternativeOptions.map(option => typeof option === 'string' ? { name: option } : option)
    : (Array.isArray(row.alternatives) ? row.alternatives.map(name => ({ name })) : []);
  return {
    dayNumber: Number(row.dayNumber) || fallbackDay,
    meal: row.meal || 'Spuntino',
    food: row.food || '',
    quantity: row.quantity ?? '',
    unit: row.unit || '',
    role: row.role || '',
    alternatives: alternativeOptions.map(option => option.name).filter(Boolean),
    alternativeOptions: alternativeOptions.map(option => ({
      clientId: option.clientId || nextAlternativeClientId(),
      name: option.name || '',
      quantity: option.quantity ?? '',
      unit: option.unit || '',
      foodCatalogItemId: option.foodCatalogItemId || null,
      foodRecipeId: option.foodRecipeId || null,
      foodSource: option.foodSource || null
    })),
    foodCatalogItemId: row.foodCatalogItemId || null,
    foodRecipeId: row.foodRecipeId || null,
    foodSource: row.foodSource || null
  };
}

export { normalizeRow as normalizeDietRow };

export function updateDietAlternative(alternatives, optionIndex, patch) {
  return alternatives.map((option, currentIndex) => currentIndex === optionIndex ? { ...option, ...patch } : option);
}

function CatalogFoodField({ row, onChange }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [externalCatalog, setExternalCatalog] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [external, setExternal] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const timer = useRef(null);
  const requestSequence = useRef(0);
  const resultsId = useId();
  const query = row.food || '';
  const isBarcode = /^\d{8,14}$/.test(query.trim());

  useEffect(() => () => { clearTimeout(timer.current); requestSequence.current += 1; }, []);

  const search = value => {
    clearTimeout(timer.current);
    requestSequence.current += 1;
    setExternal(null);
    setLookupError('');
    setCatalog([]);
    setExternalCatalog([]);
    setRecipes([]);
    setActiveIndex(-1);
    if (value.trim().length < 2) {
      setLoading(false);
      return;
    }
    timer.current = setTimeout(async () => {
      const requestId = requestSequence.current;
      setLoading(true);
      try {
        const [foods, foundRecipes] = await Promise.all([foodsApi.search(value.trim(), 6), foodRecipesApi.getAll(value.trim())]);
        if (requestId !== requestSequence.current) return;
        setCatalog(foods?.local || []);
        setExternalCatalog(foods?.external || []);
        setRecipes((foundRecipes || []).slice(0, 4));
      } catch {
        if (requestId !== requestSequence.current) return;
        setLookupError('Catalogo momentaneamente non disponibile. Puoi continuare a scrivere manualmente.');
      } finally {
        if (requestId === requestSequence.current) setLoading(false);
      }
    }, 260);
  };

  const selectFood = item => {
    requestSequence.current += 1;
    setLoading(false);
    onChange({
      food: item.name,
      foodCatalogItemId: item.id || null,
      foodRecipeId: null,
      foodSource: item.source || 'Catalogo'
    });
    setCatalog([]);
    setExternalCatalog([]);
    setRecipes([]);
    setExternal(null);
    setActiveIndex(-1);
    setOpen(false);
  };

  const selectRecipe = recipe => {
    requestSequence.current += 1;
    setLoading(false);
    onChange({ food: recipe.name, foodRecipeId: recipe.id, foodCatalogItemId: null, foodSource: 'Ricetta' });
    setCatalog([]);
    setExternalCatalog([]);
    setRecipes([]);
    setExternal(null);
    setActiveIndex(-1);
    setOpen(false);
  };

  const importSearchResult = async item => {
    const barcode = item.barcode;
    if (!barcode) return;
    const requestId = ++requestSequence.current;
    setLoading(true);
    setLookupError('');
    try {
      const imported = await foodsApi.importBarcode(barcode);
      if (requestId !== requestSequence.current) return;
      selectFood(imported);
    } catch (error) {
      if (requestId === requestSequence.current) setLookupError(error.message || 'Non è stato possibile importare il prodotto nel catalogo.');
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  };

  const lookupBarcode = async () => {
    const barcode = query.trim();
    const requestId = ++requestSequence.current;
    setLoading(true);
    setLookupError('');
    try {
      const item = await foodsApi.lookupBarcode(barcode);
      if (requestId !== requestSequence.current) return;
      setExternal(item);
      setOpen(true);
    } catch (error) {
      if (requestId === requestSequence.current) setLookupError(error.message || 'Prodotto non trovato in Open Food Facts.');
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  };

  const importExternal = async () => {
    const barcode = external?.barcode || external?.externalId || query.trim();
    const requestId = ++requestSequence.current;
    setLoading(true);
    setLookupError('');
    try {
      const item = await foodsApi.importBarcode(barcode);
      if (requestId !== requestSequence.current) return;
      selectFood(item);
    } catch (error) {
      if (requestId === requestSequence.current) setLookupError(error.message || 'Non è stato possibile importare il prodotto.');
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  };

  const options = useMemo(() => [
    ...catalog.map(item => ({ kind: 'food', item })),
    ...externalCatalog.map(item => ({ kind: 'external', item })),
    ...recipes.map(item => ({ kind: 'recipe', item }))
  ], [catalog, externalCatalog, recipes]);

  const activateOption = index => {
    const option = options[index];
    if (!option || loading) return;
    if (option.kind === 'food') selectFood(option.item);
    else if (option.kind === 'external') importSearchResult(option.item);
    else selectRecipe(option.item);
  };

  const closeResults = () => {
    setOpen(false);
    setActiveIndex(-1);
  };

  return <div className="diet-food-field">
    <div className="diet-food-input-wrap">
      <Icon name="search"/>
      <input
        value={query}
        placeholder="Cerca alimento, pietanza o barcode"
        aria-label="Alimento o pietanza"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open && (query.trim().length >= 2 || Boolean(lookupError))}
        aria-controls={resultsId}
        aria-activedescendant={activeIndex >= 0 ? `${resultsId}-option-${activeIndex}` : undefined}
        onFocus={() => { setOpen(true); search(query); }}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            closeResults();
            return;
          }
          if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && options.length) {
            event.preventDefault();
            setOpen(true);
            setActiveIndex(current => event.key === 'ArrowDown'
              ? (current + 1) % options.length
              : (current <= 0 ? options.length - 1 : current - 1));
            return;
          }
          if (event.key === 'Enter' && activeIndex >= 0) {
            event.preventDefault();
            activateOption(activeIndex);
          }
        }}
        onChange={event => {
          const food = event.target.value;
          onChange({ food, foodCatalogItemId: null, foodRecipeId: null, foodSource: null });
          setOpen(true);
          search(food);
        }}
      />
      {row.foodSource && <span className="diet-food-source"><Icon name="check"/>{row.foodSource === 'OpenFoodFacts' ? 'OFF' : row.foodSource}</span>}
    </div>
    {open && (query.trim().length >= 2 || lookupError) && <div className="diet-food-results" id={resultsId} role="listbox" aria-label="Risultati del catalogo">
      <div className="diet-food-results-head">
        <span role="status" aria-live="polite">{loading ? 'Ricerca in corso…' : 'Catalogo professionale'}</span>
        <button type="button" onClick={closeResults} aria-label="Chiudi risultati">Chiudi</button>
      </div>
      {catalog.map((item, index) => <button type="button" id={`${resultsId}-option-${index}`} role="option" aria-selected={activeIndex === index} className="diet-food-result" key={item.id || item.externalId} onMouseEnter={() => setActiveIndex(index)} onClick={() => selectFood(item)} disabled={loading}>
        {item.imageUrl ? <img src={item.imageUrl} alt=""/> : <span className="diet-food-thumb">A</span>}
        <span><strong>{item.name}</strong><small>{[item.brand, item.energyKcal100g != null ? `${item.energyKcal100g} kcal/100 g` : null].filter(Boolean).join(' · ') || 'Alimento del catalogo'}</small></span>
        <em>Alimento</em>
      </button>)}
      {externalCatalog.map((item, itemIndex) => {
        const index = catalog.length + itemIndex;
        return <button type="button" id={`${resultsId}-option-${index}`} role="option" aria-selected={activeIndex === index} className="diet-food-result external" key={`off-${item.barcode}`} onMouseEnter={() => setActiveIndex(index)} onClick={() => importSearchResult(item)} disabled={loading}>
        {item.imageUrl ? <img src={item.imageUrl} alt="" loading="lazy"/> : <span className="diet-food-thumb external">OFF</span>}
        <span><strong>{item.name}</strong><small>{[item.brand, item.energyKcal100g != null ? `${item.energyKcal100g} kcal/100 g` : null].filter(Boolean).join(' · ') || 'Scheda Open Food Facts'}</small></span>
        <em>Importa</em>
      </button>;
      })}
      {recipes.map((recipe, itemIndex) => {
        const index = catalog.length + externalCatalog.length + itemIndex;
        return <button type="button" id={`${resultsId}-option-${index}`} role="option" aria-selected={activeIndex === index} className="diet-food-result" key={recipe.id} onMouseEnter={() => setActiveIndex(index)} onClick={() => selectRecipe(recipe)} disabled={loading}>
        <span className="diet-food-thumb recipe">R</span>
        <span><strong>{recipe.name}</strong><small>{recipe.ingredients?.length || 0} ingredienti · {recipe.servings || 1} porzioni</small></span>
        <em>Ricetta</em>
      </button>;
      })}
      {!loading && !catalog.length && !externalCatalog.length && !recipes.length && !isBarcode && <p className="diet-food-empty">Nessuna corrispondenza. Il testo rimane valido come voce manuale.</p>}
      {isBarcode && !external && <button type="button" className="diet-barcode-action" onClick={lookupBarcode} disabled={loading}><Icon name="barcode"/>Cerca {query} su Open Food Facts</button>}
      {external && <div className="diet-external-result">
        {external.imageUrl && <img src={external.imageUrl} alt=""/>}
        <span><strong>{external.name}</strong><small>{[external.brand, external.energyKcal100g != null ? `${external.energyKcal100g} kcal/100 g` : null].filter(Boolean).join(' · ')}</small></span>
        <button type="button" onClick={importExternal} disabled={loading}>Importa e usa</button>
      </div>}
      {lookupError && <p className="diet-food-error" role="alert">{lookupError}</p>}
    </div>}
  </div>;
}

function MealSection({ meal, rows, setDiet }) {
  const update = (index, patch) => setDiet(value => ({ ...value, plan: value.plan.map((row, rowIndex) => rowIndex === index ? { ...normalizeRow(row), ...patch } : row) }));
  const updateAlternatives = (index, alternatives) => update(index, {
    alternatives: alternatives.map(option => option.name).filter(Boolean),
    alternativeOptions: alternatives
  });
  const patchAlternative = (index, alternatives, optionIndex, patch) => updateAlternatives(index,
    updateDietAlternative(alternatives, optionIndex, patch));
  const addAlternative = (index, alternatives) => {
    if (alternatives.length >= 4) return;
    updateAlternatives(index, [...alternatives, { clientId: nextAlternativeClientId(), name: '', quantity: '', unit: '', foodCatalogItemId: null, foodRecipeId: null, foodSource: null }]);
  };
  const removeAlternative = (index, alternatives, optionIndex) => updateAlternatives(index,
    alternatives.filter((_, currentIndex) => currentIndex !== optionIndex));
  const remove = index => setDiet(value => ({ ...value, plan: value.plan.filter((_, rowIndex) => rowIndex !== index) }));
  const renameMeal = nextMeal => setDiet(value => ({ ...value, plan: value.plan.map((row, rowIndex) => rows.some(item => item.index === rowIndex) ? { ...row, meal: nextMeal } : row) }));
  const addComponent = () => setDiet(value => ({ ...value, plan: [...value.plan, normalizeRow({ dayNumber: rows[0].row.dayNumber, meal })] }));

  return <section className="diet-meal-section">
    <header className="diet-meal-heading">
      <span className="diet-meal-index">{String(mealOrder.indexOf(meal) + 1).padStart(2, '0')}</span>
      <select aria-label={`Nome del pasto ${meal}`} value={meal} onChange={event => renameMeal(event.target.value)}>
        {mealOrder.map(option => <option key={option}>{option}</option>)}
      </select>
      <span>{rows.filter(({ row }) => row.food.trim()).length} componenti</span>
    </header>
    <div className="diet-component-labels" aria-hidden="true"><span>Alimento o ricetta</span><span>Ruolo</span><span>Quantità</span><span>Unità</span><span/></div>
    {rows.map(({ row, index }) => {
      const normalized = normalizeRow(row);
      return <div className="diet-component-row" key={`${row.dayNumber}-${meal}-${index}`}>
        <CatalogFoodField row={normalized} onChange={patch => update(index, patch)}/>
        <select aria-label={`Ruolo clinico di ${row.food || 'alimento'}`} value={row.role || ''} onChange={event => update(index, { role: event.target.value })}>{roles.map(role => <option key={role} value={role}>{role || 'Ruolo'}</option>)}</select>
        <input aria-label={`Quantità di ${row.food || 'alimento'}`} inputMode="decimal" placeholder="es. 80" value={row.quantity ?? ''} onChange={event => update(index, { quantity: event.target.value })}/>
        <select aria-label={`Unità di ${row.food || 'alimento'}`} value={row.unit || ''} onChange={event => update(index, { unit: event.target.value })}>{units.map(unit => <option key={unit} value={unit}>{unit || 'Unità'}</option>)}</select>
        <button type="button" className="diet-icon-button danger" aria-label={`Rimuovi ${row.food || 'componente'}`} onClick={() => remove(index)}><Icon name="trash"/></button>
        <div className="diet-alternative-editor" aria-label={`Alternative equivalenti per ${row.food || 'alimento'}`}>
          <div className="diet-alternative-heading">
            <span>Alternative equivalenti</span>
            <button type="button" onClick={() => addAlternative(index, normalized.alternativeOptions)} disabled={normalized.alternativeOptions.length >= 4}><Icon name="plus"/>Aggiungi</button>
          </div>
          {!!normalized.alternativeOptions.length && <div className="diet-alternative-rows">
            {normalized.alternativeOptions.map((option, optionIndex) => <div className={option.foodCatalogItemId || option.foodRecipeId ? 'diet-alternative-row is-grounded' : 'diet-alternative-row'} key={option.clientId}>
              <CatalogFoodField row={{ food: option.name, foodCatalogItemId: option.foodCatalogItemId, foodRecipeId: option.foodRecipeId, foodSource: option.foodSource }} onChange={patch => {
                const { food, ...catalogPatch } = patch;
                patchAlternative(index, normalized.alternativeOptions, optionIndex, { ...catalogPatch, name: food });
              }}/>
              <input aria-label={`Quantità alternativa ${optionIndex + 1}`} inputMode="decimal" placeholder="Quantità" value={option.quantity ?? ''} onChange={event => patchAlternative(index, normalized.alternativeOptions, optionIndex, { quantity: event.target.value })}/>
              <select aria-label={`Unità alternativa ${optionIndex + 1}`} value={option.unit || ''} onChange={event => patchAlternative(index, normalized.alternativeOptions, optionIndex, { unit: event.target.value })}>{units.map(unit => <option key={unit} value={unit}>{unit || 'Unità'}</option>)}</select>
              <button type="button" className="diet-icon-button danger" aria-label={`Rimuovi alternativa ${option.name || optionIndex + 1}`} onClick={() => removeAlternative(index, normalized.alternativeOptions, optionIndex)}><Icon name="trash"/></button>
            </div>)}
          </div>}
          {!normalized.alternativeOptions.length && <small>Nessuna alternativa. Aggiungine una e collegala al catalogo quando disponibile.</small>}
        </div>
      </div>;
    })}
    <button type="button" className="diet-add-component" onClick={addComponent}><Icon name="plus"/>Aggiungi componente al pasto</button>
  </section>;
}

export function StructuredDietEditor({ diet, setDiet, dietAssisted, dietAiReview, savingDiet, onSubmit }) {
  const normalizedPlan = useMemo(() => (diet.plan || []).map(normalizeRow), [diet.plan]);
  const dayCount = Math.max(1, ...normalizedPlan.map(row => row.dayNumber));
  const [activeDay, setActiveDay] = useState(1);
  const [showSettings, setShowSettings] = useState(!diet.name?.trim());
  useEffect(() => { if (activeDay > dayCount) setActiveDay(dayCount); }, [activeDay, dayCount]);
  const dayRows = normalizedPlan.map((row, index) => ({ row, index })).filter(item => item.row.dayNumber === activeDay);
  const meals = [...new Set(dayRows.map(item => item.row.meal))].sort((a, b) => mealOrder.indexOf(a) - mealOrder.indexOf(b));
  const completed = dayRows.filter(({ row }) => row.food.trim()).length;
  const incomplete = dayRows.filter(({ row }) => row.food.trim() && (!String(row.quantity).trim() || !row.unit)).length;
  const globalCompleted = normalizedPlan.filter(row => row.food.trim()).length;
  const globalIncomplete = normalizedPlan.filter(row => row.food.trim() && (!String(row.quantity).trim() || !row.unit)).length;
  const catalogGrounded = normalizedPlan.filter(row => row.food.trim() && (row.foodCatalogItemId || row.foodRecipeId)).length;
  const alternativeOptions = normalizedPlan.flatMap(row => row.alternativeOptions || []);
  const groundedAlternatives = alternativeOptions.filter(option => option.foodCatalogItemId || option.foodRecipeId).length;

  const addDay = () => {
    const next = dayCount + 1;
    setDiet(value => ({ ...value, plan: [...value.plan, ...['Colazione', 'Pranzo', 'Cena'].map(meal => normalizeRow({ dayNumber: next, meal }))] }));
    setActiveDay(next);
  };
  const duplicateDay = () => {
    const next = dayCount + 1;
    const clones = dayRows.map(({ row }) => {
      const normalized = normalizeRow(row);
      return { ...normalized, dayNumber: next, alternatives: [...normalized.alternatives], alternativeOptions: normalized.alternativeOptions.map(option => ({ ...option })) };
    });
    setDiet(value => ({ ...value, plan: [...value.plan, ...clones] }));
    setActiveDay(next);
  };
  const removeDay = () => {
    if (dayCount === 1) return;
    if (!window.confirm(`Eliminare il giorno ${activeDay} e tutti i pasti che contiene?`)) return;
    setDiet(value => ({ ...value, plan: value.plan.filter(row => Number(row.dayNumber) !== activeDay).map(row => ({ ...row, dayNumber: Number(row.dayNumber) > activeDay ? Number(row.dayNumber) - 1 : Number(row.dayNumber) })) }));
    setActiveDay(Math.max(1, activeDay - 1));
  };
  const addMeal = () => {
    const nextMeal = mealOrder.find(meal => !meals.includes(meal)) || 'Altro';
    setDiet(value => ({ ...value, plan: [...value.plan, normalizeRow({ dayNumber: activeDay, meal: nextMeal })] }));
  };

  return <form className="clinic-panel clinic-form diet-editor diet-studio" onSubmit={onSubmit}>
    <div className="diet-studio-hero">
      <div className="diet-studio-title">
        <span className="diet-ai-mark"><Icon name={dietAssisted ? 'spark' : 'check'}/></span>
        <div><p>{dietAssisted ? 'Sibilla AI · Bozza clinica' : 'Composizione manuale'}</p><h2>{diet.name || 'Nuovo piano alimentare'}</h2><span>{dietAssisted ? 'Pasti, catalogo e alternative già organizzati; verifica prima di salvare' : 'Costruisci il piano per pasti e giornate'}</span></div>
      </div>
      <div className="diet-studio-status"><strong>{dayCount}</strong><span>{dayCount === 1 ? 'giorno' : 'giorni'}</span><i/><strong>{normalizedPlan.filter(row => row.food.trim()).length}</strong><span>componenti</span></div>
    </div>

    <button type="button" className="diet-settings-toggle" aria-expanded={showSettings} onClick={() => setShowSettings(value => !value)}>
      <span><strong>Impostazioni del piano</strong><small>Nome, metodo, periodo e stato clinico</small></span><span>{showSettings ? 'Nascondi' : 'Modifica'}</span>
    </button>
    {showSettings && <div className="clinic-grid three diet-settings-grid">
      <label>Nome piano<input required value={diet.name} onChange={event => setDiet(value => ({ ...value, name: event.target.value }))}/></label>
      <label>Tipo<select value={diet.type} onChange={event => setDiet(value => ({ ...value, type: event.target.value }))}><option>Giornaliera</option><option>Settimanale</option><option>Frequenza giorni</option></select></label>
      <label>Metodo<select value={diet.planningMethod} onChange={event => setDiet(value => ({ ...value, planningMethod: event.target.value, targetCalories: event.target.value === 'EnergyTarget' ? value.targetCalories : '' }))}><option value="PortionsAndFrequencies">Porzioni e frequenze</option><option value="EnergyTarget">Obiettivo energetico</option></select></label>
      {diet.planningMethod === 'EnergyTarget' && <label>Obiettivo kcal<input type="number" min="500" required value={diet.targetCalories} onChange={event => setDiet(value => ({ ...value, targetCalories: event.target.value }))}/></label>}
      <label>Inizio<input type="date" value={diet.startsOn} onChange={event => setDiet(value => ({ ...value, startsOn: event.target.value }))}/></label>
      <label>Fine<input type="date" value={diet.endsOn} onChange={event => setDiet(value => ({ ...value, endsOn: event.target.value }))}/></label>
      <label>Stato al salvataggio<span className="diet-status-readonly"><Icon name="check"/>Bozza clinica da verificare</span></label>
    </div>}

    <div className="diet-studio-layout">
      <aside className="diet-day-rail" aria-label="Giornate del piano">
        <div className="diet-day-rail-title"><span>Giornate</span><strong>{dayCount}</strong></div>
        <div className="diet-day-list">
          {Array.from({ length: dayCount }, (_, index) => index + 1).map(day => {
            const count = normalizedPlan.filter(row => row.dayNumber === day && row.food.trim()).length;
            return <button type="button" key={day} className={activeDay === day ? 'active' : ''} aria-current={activeDay === day ? 'step' : undefined} onClick={() => setActiveDay(day)}><span>Giorno <b>{day}</b></span><small>{count ? `${count} voci` : 'Da compilare'}</small></button>;
          })}
        </div>
        <button type="button" className="diet-day-add" onClick={addDay}><Icon name="plus"/>Nuovo giorno</button>
      </aside>

      <section className="diet-day-canvas" key={activeDay} aria-labelledby="diet-day-title">
        <header className="diet-day-heading">
          <div><p>Giornata {activeDay} di {dayCount}</p><h3 id="diet-day-title">Schema alimentare</h3><span>{completed ? `${completed} componenti inseriti` : 'Inizia dai pasti principali'}</span></div>
          <div className="diet-day-actions"><button type="button" onClick={duplicateDay}>Duplica giornata</button><button type="button" className="danger" onClick={removeDay} disabled={dayCount === 1}>Elimina</button></div>
        </header>
        {incomplete > 0 && <div className="diet-inline-check" role="status"><span>{incomplete}</span><p><strong>Componenti da completare</strong>Aggiungi quantità e unità prima di consegnare il piano.</p></div>}
        <DietNutritionSummary plan={normalizedPlan} activeDay={activeDay} targetCalories={diet.targetCalories} planningMethod={diet.planningMethod}/>
        <div className="diet-meal-stack">
          {meals.map(meal => <MealSection key={meal} meal={meal} rows={dayRows.filter(item => item.row.meal === meal)} setDiet={setDiet}/>) }
          {!meals.length && <div className="diet-day-empty"><strong>Questa giornata è vuota</strong><span>Aggiungi un pasto per iniziare a comporla.</span></div>}
        </div>
        <button type="button" className="diet-add-meal" onClick={addMeal}><Icon name="plus"/>Aggiungi un altro pasto</button>
      </section>

      <aside className="diet-quality-rail">
        <div className="diet-quality-head"><Icon name={dietAssisted ? 'spark' : 'check'}/><div><strong>Verifica clinica</strong><span>{dietAssisted ? 'Controllo della proposta IA' : 'Controllo del piano'}</span></div></div>
        <dl>
          <div><dt>Piano completo</dt><dd className={globalCompleted ? 'ready' : ''}>{globalCompleted ? `${globalCompleted} voci` : 'Da iniziare'}</dd></div>
          <div><dt>Quantità globali</dt><dd className={globalIncomplete ? 'warning' : 'ready'}>{globalIncomplete ? `${globalIncomplete} mancanti` : 'Complete'}</dd></div>
          <div><dt>Voci dal catalogo</dt><dd className={catalogGrounded ? 'ready' : 'warning'}>{globalCompleted ? `${catalogGrounded}/${globalCompleted}` : 'Da iniziare'}</dd></div>
          <div><dt>Alternative collegate</dt><dd className={groundedAlternatives ? 'ready' : alternativeOptions.length ? 'warning' : ''}>{alternativeOptions.length ? `${groundedAlternatives}/${alternativeOptions.length}` : 'Nessuna'}</dd></div>
          <div><dt>Metodo</dt><dd>{diet.planningMethod === 'EnergyTarget' ? 'Energetico' : 'Porzioni'}</dd></div>
          <div><dt>Stato al salvataggio</dt><dd>Bozza</dd></div>
        </dl>
        {dietAiReview?.protocol && <div className="diet-quality-note"><strong>Protocollo</strong><span>{dietAiReview.protocol.name}</span><small>Versione {dietAiReview.protocol.version}</small></div>}
        {dietAiReview?.missingContext?.length > 0 && <div className="diet-quality-warning"><strong>Dati da integrare</strong><span>{dietAiReview.missingContext.join(', ')}</span></div>}
        <label className="diet-quality-notes">Note cliniche<textarea value={diet.notes || ''} placeholder="Motivazioni, indicazioni pratiche, follow-up…" onChange={event => setDiet(value => ({ ...value, notes: event.target.value }))}/></label>
      </aside>
    </div>

    {dietAiReview?.warnings?.length > 0 && <div className="diet-review-safety"><strong>Avvertenze sulla bozza</strong><ul>{dietAiReview.warnings.map(warning => <li key={warning}>{warning}</li>)}</ul></div>}
    <div className="diet-studio-footer">
      <div><strong>La nutrizionista mantiene il controllo</strong><span>La bozza non viene attivata né consegnata automaticamente.</span></div>
      <button className="clinic-button" disabled={savingDiet || !diet.name?.trim()}>{savingDiet ? 'Salvataggio…' : !diet.name?.trim() ? 'Inserisci il nome del piano' : 'Salva bozza clinica'}</button>
    </div>
  </form>;
}

export default StructuredDietEditor;
